/* eslint-disable @typescript-eslint/no-explicit-any */
import Api from '@sendsprint/api-types';
import { SetAddressFormData } from '@src/pages/auth/register/types';
import { Location } from 'react-router-dom';

/* eslint-disable @typescript-eslint/no-namespace */
// eslint-disable-next-line @typescript-eslint/prefer-namespace-keyword
module ClientApi {
  /** This refers to the useLocation custom type */
  export type AppLocationState = Location;

  /**
   * Response Types
   */
  export namespace Response {
    export interface PaginatedDataResponse {
      count: number;
      hasNextPage: boolean;
      pageNumber: number;
      totalItemCount: number;
    }

    export interface ResponseBody {
      httpStatusCode: number;
      hasError: boolean;
      title?: string;
      responseMessage: {
        title: string;
        actionMessage: string;
      };
      responseCode: number;
    }
  }

  /**
   * General types
   */
  export namespace General {
    export type Client = 'receiver' | 'sender' | 'white-label';

    export type CountryData = {
      id: number;
      name: string;
      telCode: string;
      initials: Api.Model.CountryInitials;
      currency: Api.Model.CurrencyCode;
    };

    export type Locales = string | string[];

    export type Bank = {
      id: number;
      code: Api.Model.BankCode;
      name: string;
    };

    export type BankBranch = {
      // id: number;
      // code: Api.Model.BranchCode;
      // name: string;
      branch_code: Api.Model.BranchCode;
      branch_name: string;
      swift_code: string | null;
      bic: string | null;
      bank_id: number;
    };

    export type ResolvedBankAccount = {
      accountNumber: string;
      accountName: string;
    };

    /**
     * Recipient type is a convenient way to map valid country/paymentType combinations. They're used instead of complex
     * conditional statements that can be difficult to manage.
     *
     * You can find the mapping and utility functions to convert both ways in @src/utils/recipient-type.ts
     */
    export enum RecipientType {
      /** Nigerian domiciliary account */
      NG_DOM = 'NG-DOM',
      /** Nigerian Virtual domiciliary account */
      NG_V_DOM = 'NG-V-DOM',
      /** Cash pickup in Nigeria */
      NG_CASH = 'NG-CASH',
      /** Nigerian Auto Domiciliary Account */
      NG_A_DOM = 'NG-A-DOM',
      /** Nigerian bank account in naira */
      NEW_NG_BANK = 'NEW-NG-BANK',
      /** Ghanaian bank account */
      GH_BANK = 'GH-BANK',
      /** Ghanaian MoMo mobile money account */
      GH_MOBILE = 'GH-MOBILE',
      /** Kenyan Bank Account */
      KE_BANK = 'KE-BANK',
      /** Kenyan Mpesa Mobile Money Account */
      KE_MOBILE = 'KE-MOBILE',
      /** South African bank account */
      ZA_BANK = 'ZA-BANK'
    }

    export type Recipient = {
      id: number;
      ownerId: number;
      type: RecipientType;

      firstName: string;
      middleName: string;
      lastName: string;
      fullName: string;

      accountNumber: string;
      bankCode: string;
      branchCode: string;
      routingNumber: string;

      address: string;

      email: string;
      phoneNumber: string;
      paymentOperator: string;

      country: Api.Model.CountryInitials;
      paymentType: Api.Model.PaymentType;
      status: Api.Model.RecipientStatus;
      dateOfBirth: string | null | undefined;
    };

    /** These fields must be filled and cannot be an empty string */
    export type RecipientFormRequiredFields =
      | 'firstName'
      | 'lastName'
      | 'address'
      | 'country'
      | 'routingNumber'
      | 'paymentType';

    /** These fields are optional and can be an empty string */
    export type RecipientFormOptionalFields =
      | 'middleName'
      | 'accountNumber'
      | 'bankCode'
      | 'branchCode'
      | 'dateOfBirth'
      // | "routingNumber"
      | 'email'
      | 'phoneNumber'
      | 'paymentOperator';

    /** Recipient payment type info */
    export type PaymentTypeInfo = {
      id: number;
      country: Api.Model.CountryInitials;
      paymentName: string;
      paymentType: Api.Model.PaymentType;
      status: Api.Model.PaymentTypeStatus;
      recipientType: RecipientType;
    };

    /** Location state when a user tries to access a protected route when logged out. We save the current location so we can redirect on login */
    export type NotLoggedInLocationState = { from: Location };

    export interface TokenDetails {
      refreshToken: string;
      token: {
        accessToken: string;
        expiryTime: string;
      };
      hash: string;
    }

    export interface AdminTokenDetails {
      SessionToken: string;
      SessionExpiryTime: string;
      SessionDuration: number;
    }

    export interface UserAddress {
      city: string;
      country: Api.Model.CountryInitials;
      postCode: string;
      street: string;
      phone: string;
      state: string | null;
      role: string | undefined;
      percentage: string | undefined;
    }

    export interface UserProfile {
      firstName: string;
      lastName: string;
      fullName: string;
      email: string;
      isEmailVerified: boolean;
      referralCode: string;
      profilePixUrl: string;
      dateOfBirth: string;
      /**
       * 1 = user hasn't filled his profile
       * 2 = user has filled his profile
       */
      userOnboardingStatus: 1 | 2;
      /**
         * 
        None = 0,
        Sender = 1,
        Receiver = 2
         */
      userCategory: number;
      /**
         * 
        None = 0,
        Sender = 1,
        Receiver = 2
        FintecOrg = 3
         */
      userCategoryType: number;
    }

    export interface BusinessProfile {
      businessName: string;
      businessLogoUrl: string;
      merchantId: number;
    }

    export interface LoggedInUser {
      user: UserProfile;
      address: UserAddress;
      tokens: TokenDetails;
      adminTokens: AdminTokenDetails | null;
      business: BusinessProfile;
    }

    export interface BusinessCountryI {
      currencyCode: Api.Model.CountryInitials;
      initial: string;
      name: string;
    }

    export interface StateI {
      name: string;
      code: string;
      id: number;
    }

    export interface BusinessTypeI {
      id: number;
      name: string;
    }

    export type BusinessIndustryI = BusinessTypeI;

    export type TransferTypesNameI = 'Individual' | 'Bulk';

    export interface TransferTypesI {
      id: number;
      name: TransferTypesNameI;
    }

    export interface SettlementPaymentTypeI {
      id: number;
      country: string;
      paymentType: string;
      paymentName: string;
    }

    /** Rate info type */
    export type RateType = {
      rate: number;
      fee: number;
      amount: number;
    };

    export type DateTimeTZ = string;

    export type Transaction = {
      id: number;
      txRef: string;
      receiveAmount: number;
      sendAmount: number;
      rate: number;
      fee: number;
      sendCurrency: Api.Model.CurrencyCode;
      receiveCurrency: Api.Model.CurrencyCode;
      securityQuestion: string;
      securityAnswer: string;
      status: Api.Model.TransactionStatus;
      createdDate: Date;
      createdDateISO: DateTimeTZ;
      updatedDate: Date;
      updatedDateISO: DateTimeTZ;
      recipientName: string;
      recipientId: number;
      ownerId: number;
      // paymentType?: Api.Model.PaymentType;
    };

    export interface ExtendedTransactionRefInfo extends Api.Model.TransactionRefInfo {
      payment_plan?: string | number;
      URL?: string;
    }

    export type TransactionRefInfo = {
      txRef: string;
      amount: number;
      payment_plan?: string | number;
      URL?: string;
    };

    export interface BulkTransaction {
      transactionReference: string;
      sourceCurrency: string;
      targetCurrency: string;
      dateCreated: string;
      status: string;
      amount: number;
      fee: number;
      chargedAmount: number;
      recipientsCount: number;
    }

    export interface SingleTransaction {
      transactionReference: string;
      amountSent: number;
      amountReceived: number;
      fee: number;
      sourceCurrency: {
        currencyCode: string;
        countryInitial: string;
        countryName: string;
      };
      targetCurrency: {
        currencyCode: string;
        countryInitial: string;
        countryName: string;
      };
      status: string;
      dateCreated: string;
      dateUpdated: string;
      transactionClassification: string;
      paymentDeliveryMethod: string;
      recipientDetails: {
        id: number;
        firstName: string;
        middleName: string;
        lastName: string;
        email: string;
        phone: string;
        accountNumber: string;
        bankCode: string;
        branchCode: string;
        routingNumber: string;
        address: string;
        country: string;
        status: string;
        paymentType: string;
        paymentOperator: string;
        dateCreated: string;
        dateUpdated: string;
        customerType: string;
        paymentDeliveryMethod: string;
        dateOfBirth: string;
      };
      paymentLinkDetails: PaymentLinkI;
    }

    export interface SettlementAccountI {
      id: number;
      accountName: string;
      accountNumber: string;
      bankName: string;
      bankCode: string;
      dateCreated: string;
      dateUpdated: string;
      status: string;
      paymentType: string;
      paymentTypeId: number;
      isPrimary: boolean;
      accountCurrencyCode: string;
      currencyPair: {
        currencyCode: string;
        countryInitial: string;
        countryName: string;
      };
    }

    export interface PaymentLinkI {
      name: string;
      description: string;
      amount: number;
      senderCurrencyCode: string;
      targetCurrencyCode: string;
      status: string;
      reference: string;
      frequencyName: string;
      dateCreated: string;
      dateUpdated: string;
      redirectUrl?: string | null;
      interval: string;
      intervalCount: string;
      frequency: number;
      customerCanCancelSubscription: boolean;
      extraFields: [];
      businessName: string;
      businessEmail: string;
      businessLogo: string;
      intervalName: string;
      duration: number;
      id: number;
    }

    export interface PaymentLinkII {
      name: string;
      description: string;
      amount: number;
      senderCurrencyCode: string;
      targetCurrencyCode: string;
      interval: string;
      intervalCount: string;
      frequency: number;
      customerCanCancelSubscription: boolean;
      status: string;
      reference: string;
      dateCreated: string;
      dateUpdated: string;
      extraFields: [];
      businessName: string;
      businessEmail: string;
      businessLogo: string;
      frequencyName: string;
      intervalName: string;
      duration: number;
      redirectUrl: null | string;
    }
  }

  /**
   * Account types
   */
  export namespace Account {
    export namespace Login {
      export interface Request {
        emailAddress: string;
        password: string;
        deviceId: string;
      }

      export interface LoginData {
        user: {
          emailAddress: string;
          hash: string;
          firstName: string;
          lastName: string;
          lastLogin: string;
          userOnboardingStatus: number;
          phoneNumber: string;
          referralCode: string;
          street: string;
          state: string;
          postCode: string;
          city: string;
          country: Api.Model.CountryInitials;
          profilePixUrl: string;
          merchantId: number;
          hasSetTransactionPin: boolean;
          businessName: string;
          businessLogoUrl: string;
          dateOfBirth: string;
          /**
           * 
          None = 0,
          Sender = 1,
          Receiver = 2
          */
          userCategory: number;
          /**
           * 
          None = 0,
          Sender = 1,
          Receiver = 2
          FintecOrg = 3
          */
          userCategoryType: number;
          percentage: string | null;
          personalRole: string | null;

          ssn: string;
          roleId: string | null;
          isBusinessOwner: boolean;
          permissions: {
            role: string;
            permissions: {
              name: string;
            }[];
          };
          businessVerificationStatus: number;
        };
        token: Omit<General.TokenDetails, 'hash' | 'adminSessionToken'>;
        // token: {
        //   refreshToken: '928680f708124cb28081c69cdeb3f785';
        //   token: {
        //     accessToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJodHRwOi8vc2NoZW1hcy54bWxzb2FwLm9yZy93cy8yMDA1LzA1L2lkZW50aXR5L2NsYWltcy9uYW1laWRlbnRpZmllciI6IjEyMjEiLCJodHRwOi8vc2NoZW1hcy54bWxzb2FwLm9yZy93cy8yMDA1LzA1L2lkZW50aXR5L2NsYWltcy9lbWFpbGFkZHJlc3MiOiJlbW1hbnVlbCt3aGl0ZWxhYmVsQHNlbmRzcHJpbnQuY29tIiwiaHR0cDovL3NjaGVtYXMueG1sc29hcC5vcmcvd3MvMjAwNS8wNS9pZGVudGl0eS9jbGFpbXMvbW9iaWxlcGhvbmUiOiIrMTkwOTk5OTk5OTkiLCJGdWxsTmFtZSI6IkVtbWFudWVsIFN0ZXBoZW4iLCJGaXJzdE5hbWUiOiJFbW1hbnVlbCIsIkxhc3ROYW1lIjoiU3RlcGhlbiIsIkRldmljZUlkIjoiTW96aWxsYS81LjAgKE1hY2ludG9zaDsgSW50ZWwgTWFjIE9TIFggMTBfMTVfNykgQXBwbGVXZWJLaXQvNTM3LjM2IChLSFRNTCwgbGlrZSBHZWNrbykgQ2hyb21lLzE0NC4wLjAuMCBTYWZhcmkvNTM3LjM2IiwiTG9naW5TdGFtcCI6ImRiNTU5NTg1MGQxYzRhOWI5YWE0OTAzYTdjNGJmYTU4IiwiUHJvZmlsZVN0YXR1cyI6IkFjdGl2ZSIsIkRldmljZVR5cGUiOiJXZWIiLCJSZWZyZXNoVG9rZW4iOiI5Mjg2ODBmNzA4MTI0Y2IyODA4MWM2OWNkZWIzZjc4NSIsIlJvbGVJZCI6IjEiLCJQZXJtaXNzaW9ucyI6IntcIlJvbGVcIjpcIkFkbWluXCIsXCJQZXJtaXNzaW9uc1wiOlt7XCJOYW1lXCI6XCJWaWV3IERhc2hib2FyZFwifSx7XCJOYW1lXCI6XCJWaWV3IFJlY2VpdmVyIFRyYW5zYWN0aW9uc1wifSx7XCJOYW1lXCI6XCJFeHBvcnQgVHJhbnNhY3Rpb25zXCJ9LHtcIk5hbWVcIjpcIlZpZXcgVHJhbnNhY3Rpb24gRGV0YWlsc1wifSx7XCJOYW1lXCI6XCJWaWV3IFNlbmRlciBUcmFuc2FjdGlvbnNcIn0se1wiTmFtZVwiOlwiVmlldyBTaW5nbGUgVHJhbnNhY3Rpb25zXCJ9LHtcIk5hbWVcIjpcIlZpZXcgQnVsayBUcmFuc2FjdGlvbnNcIn0se1wiTmFtZVwiOlwiSW5pdGlhdGUgc2VuZCBtb25leVwifSx7XCJOYW1lXCI6XCJWaWV3IFNldHRsZW1lbnRzXCJ9LHtcIk5hbWVcIjpcIkV4cG9ydCBTZXR0bGVtZW50c1wifSx7XCJOYW1lXCI6XCJWaWV3IFNldHRsZW1lbnQgRGV0YWlsc1wifSx7XCJOYW1lXCI6XCJWaWV3IEN1c3RvbWVyc1wifSx7XCJOYW1lXCI6XCJFeHBvcnQgQ3VzdG9tZXJzXCJ9LHtcIk5hbWVcIjpcIlZpZXcgQXBpIGtleXMgYW5kIHdlYmhvb2tzXCJ9LHtcIk5hbWVcIjpcIlZpZXcgQXBpIGtleXNcIn0se1wiTmFtZVwiOlwiVmlldyBXZWJob29rc1wifSx7XCJOYW1lXCI6XCJDcmVhdGUgQXBpIEtleVwifSx7XCJOYW1lXCI6XCJDcmVhdGUgV2ViaG9va1wifSx7XCJOYW1lXCI6XCJEZWFjdGl2YXRlIEFwaSBLZXlcIn0se1wiTmFtZVwiOlwiRGVhY3RpdmF0ZSBXZWJob29rXCJ9LHtcIk5hbWVcIjpcIlZpZXcgU2V0dXAgR3VpZGVcIn0se1wiTmFtZVwiOlwiVmlldyBQYXltZW50IExpbmtzXCJ9LHtcIk5hbWVcIjpcIkNyZWF0ZSBQYXltZW50IExpbmtcIn0se1wiTmFtZVwiOlwiVmlldyBQYXltZW50IExpbmsgRGV0YWlsc1wifSx7XCJOYW1lXCI6XCJFZGl0IFBheW1lbnQgTGlua1wifSx7XCJOYW1lXCI6XCJEZWxldGUgUGF5bWVudCBMaW5rXCJ9LHtcIk5hbWVcIjpcIkVuYWJsZS9EaXNhYmxlIFBheW1lbnQgTGlua1wifSx7XCJOYW1lXCI6XCJWaWV3IEFkbWluXCJ9LHtcIk5hbWVcIjpcIlZpZXcgVGVhbSBNZW1iZXJzXCJ9LHtcIk5hbWVcIjpcIkludml0ZSBUZWFtIE1lbWJlclwifSx7XCJOYW1lXCI6XCJWaWV3IFRlYW0gTWF0ZSBEZXRhaWxzXCJ9LHtcIk5hbWVcIjpcIlVwZGF0ZSBUZWFtIE1lbWJlciBSb2xlXCJ9LHtcIk5hbWVcIjpcIkVuYWJsZS9EaXNhYmxlIFRlYW0gTWVtYmVyXCJ9LHtcIk5hbWVcIjpcIlJlc2VuZCBUZWFtIE1lbWJlciBJbnZpdGVcIn0se1wiTmFtZVwiOlwiQ2FuY2VsIFRlYW0gTWVtYmVyXCJ9LHtcIk5hbWVcIjpcIlZpZXcgUm9sZXNcIn0se1wiTmFtZVwiOlwiQ3JlYXRlIFJvbGVcIn0se1wiTmFtZVwiOlwiVmlldyBSb2xlIERldGFpbHNcIn0se1wiTmFtZVwiOlwiRWRpdCBSb2xlIE5hbWVcIn0se1wiTmFtZVwiOlwiVXBkYXRlIFJvbGUgUGVybWlzc2lvbnNcIn0se1wiTmFtZVwiOlwiVmlldyBBY3Rpdml0eSBMb2dzXCJ9LHtcIk5hbWVcIjpcIlZpZXcgQnVzaW5lc3MgUHJlZmVyZW5jZXNcIn0se1wiTmFtZVwiOlwiVmlldyBTZXR0bGVtZW50IEFjY291bnRzXCJ9LHtcIk5hbWVcIjpcIkFkZCBTZXR0bGVtZW50IEFjY291bnRzXCJ9LHtcIk5hbWVcIjpcIlZpZXcgU2V0dGxlbWVudCBBY2NvdW50IERldGFpbHNcIn0se1wiTmFtZVwiOlwiRWRpdCBTZXR0bGVtZW50IEFjY291bnRcIn0se1wiTmFtZVwiOlwiRGVsZXRlIFNldHRsZW1lbnQgQWNjb3VudFwifSx7XCJOYW1lXCI6XCJDcmVhdGUgQnVsayBUcmFuc2ZlclwifV19IiwibmJmIjoxNzY5NTkzNjkyLCJleHAiOjE3Njk1OTQ1OTJ9.UfHTl6el_u2ZyfSF2U9hYzLUvEEhREGifWE72QsuFyM';
        //     expiryTime: '2026-01-28T10:03:12.1516389+00:00';
        //   };
        //   adminRefreshToken: null;
        // };
        adminLoginResponse: {
          emailAddress: string;
          firstName: string;
          lastName: string;
          sessionToken: string;
          sessionExpiryTime: string;
          sessionDuration: number;
          lastLogin: string;
          roleName: string;
          roleId: number;
          rolePermissions: {
            sectionName: string;
            permissions: {
              hasPermission: boolean;
              id: number;
              name: string;
              description: string;
            }[];
          }[];
        };
      }

      export interface Response extends Response.ResponseBody {
        data: LoginData;
      }
    }

    export namespace LoginUser {
      export type Request = Omit<
        Api.Account.Login.Request,
        'ApplicationID' | 'SecretKey' | 'LoginType'
      >;

      export interface Data extends Api.Model.User {
        UserType: string;
      }

      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: [Data];
      }
    }

    export namespace Register {
      export interface Request
        extends Omit<Api.Account.Register.Request, 'ApplicationID' | 'SecretKey' | 'LoginType'> {
        Referrer: string;
        SignUpSourceId: string;
      }
    }

    export namespace SubmitUserCategory {
      export interface Request {
        emailAddress: string;
        /**
         * 
        None = 0,
        Sender = 1,
        Receiver = 2
         */
        userCategory: number;
        /**
         * 
        None = 0,
        Sender = 1,
        Receiver = 2
        FintecOrg = 3
         */
        userCategoryType: number;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace RefreshToken {
      export interface Request {
        refreshToken: string;
        // adminSessionToken: string;
      }

      export interface Response extends Response.ResponseBody {
        data: General.TokenDetails;
      }
    }

    export namespace Logout {
      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace SignUpSource {
      export interface SignUpSourceData {
        Id: number;
        Name: string;
      }
      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: SignUpSourceData[];
      }
    }

    export namespace GenerateOtp {
      export interface GenerateOtpI {
        phone: string;
        email: string;
        hash: string;
        otp: string;
        /**
         * None = 0,
        Onboarding = 1
         */
        purpose?: 0 | 1;
      }

      export type Request = Omit<GenerateOtpI, 'otp'>;
    }

    export namespace VerifyOtp {
      export type Request = GenerateOtp.GenerateOtpI;
    }

    export namespace SetAddress {
      export interface Request extends SetAddressFormData {
        email?: string;
        hash?: string;
      }
    }

    export namespace ResetPasswordByEmail {
      export interface Request {
        Email: string;
      }
    }

    export namespace ChangePassword {
      export type Request = Omit<Api.Account.ChangePassword.Request, 'ApplicationID' | 'SecretKey'>;
    }

    export namespace ChangePasswordFromProfile {
      export type Request = Omit<
        Api.Account.ChangePasswordFromProfile.Request,
        'ApplicationID' | 'SecretKey'
      >;
    }

    export namespace LoadChargeRoute {
      export interface ChargeRouteI {
        country: string;
        gateWay: string;
        description: string;
        interval: string;
      }

      export interface Request {
        country: string;
        /**
         * Registered = 1
         * Link = 2
         */
        paymentUserType: 1 | 2;
      }

      export interface Response extends Response.ResponseBody {
        data: {
          routes: ChargeRouteI[];
        };
      }
    }

    export namespace GetVeriffStatus {
      export interface Request {
        email: string;
      }
    }

    export namespace VeriffSessions {
      export interface Request {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any;
      }
    }

    export namespace VeriffSessionsTest {
      export interface Request {
        email: string;
      }
    }

    export namespace IdIsActive {
      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: any;
      }
    }
  }

  /**
   * Application types
   */
  export namespace Application {
    export namespace GetBusinessCountries {
      export interface Request {
        /**
         * Sender = 1, 
           Recipient = 2
         */
        BusinessCountry: 1 | 2 | 0;
      }

      interface Data extends Response.PaginatedDataResponse {
        countries: General.BusinessCountryI[];
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace GetStatesByCountry {
      interface Data extends Response.PaginatedDataResponse {
        states: General.StateI[];
      }

      export interface Request {
        countryCode: string;
        getAllStates?: boolean;
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace GetBusinessTypes {
      interface Data extends Response.PaginatedDataResponse {
        businessTypes: General.BusinessTypeI[];
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace GetBusinessTypesByCountry {
      export interface Request {
        countryCode: string;
      }

      interface Data extends Response.PaginatedDataResponse {
        businessTypes: General.BusinessTypeI[];
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace GetBusinessIndustries {
      interface Data extends Response.PaginatedDataResponse {
        businessIndustries: General.BusinessIndustryI[];
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace GetTransferTypes {
      interface Data extends Response.PaginatedDataResponse {
        transferTypes: General.TransferTypesI[];
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace GetSettlementPaymentTypesByCountry {
      export interface Request {
        CountryCode: string;
      }

      interface Data extends Response.PaginatedDataResponse {
        settlementPaymentTypes: General.SettlementPaymentTypeI[];
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }
  }

  /**
   * Onboarding types
   */
  export namespace Onboarding {
    export namespace SubmitBusinessDetails {
      type ControlPersonInformationType = Partial<{
        Address: string;
        City: string;
        StateId: string;
        FirstName: string;
        LastName: string;
        DateOfBirth: string;
        SSN: string;
        IdType: string | number;
        GovernmentId: File;
      }>;

      export type BusinessAddress = Partial<{
        Address: string;
        City: string;
        ZipCode: string;
        StateId: string;
        RegisteredAddressEvidence: File;
      }>;

      export type OtherComplianceDoc = Partial<{
        Title: string;
        AmlDocumentation: File;
      }>;

      export interface Request {
        businessInformation: Partial<{
          LogoFile: File;
          BusinessTypeId: string;
          BusinessIndustryId: string;
          Name: string;
          PhoneNumber: string;
          EmailAddress: string;
          About: string;
          TradeName: string;
          CorporateSecretaryName: string;
          WebsiteUrl: string;
          Address: string;
          City: string;
          CountryCode: string;
          StateId: string;
          RegisteredAddressEvidence: File;
          ZipCode: string;
          BusinessAddress: BusinessAddress[];
        }>;
        businessDocuments: Partial<{
          CompanyRegistrationNumber: string;
          TaxIdentificationNumber: string;
          ShareHolderDocument: File;
          CertificateOfIncorporation: File;
          DBACertificate: File;
          UBOCertificate: File;
          ArticleOfIncoporationCertificate: File;
          ComplianceCertificate: File;
          AgencyApplicationForm: File;
          W9OrSS4Form: File;
          SignedAgreement: File;
          AmlDocumentation: File;
          CriminalBackgroundCheck: File;
          AgentOnboardingKYBForm: File;
          OtherComplianceDocs: OtherComplianceDoc[];
        }>;
        controlPersonInformation: ControlPersonInformationType[];
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace UpdateBusinessLocation {
      export interface Request {
        emailAddress: string;
        state: string;
        city: string;
        street: string;
        postCode: string;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace SubmitBusinessInformation {
      type BusinessAddress = {
        Address: string;
        City: string;
        ZipCode: string;
        StateId: string;
        RegisteredAddressEvidence?: File;
      };
      export interface Request {
        BusinessType?: number;
        LogoFile: File;
        BusinessTypeId: string;
        BusinessIndustryId: string;
        Name: string;
        PhoneNumber: string;
        EmailAddress: string;
        About: string;
        TradeName?: string;
        WebsiteUrl?: string;
        Address?: BusinessAddress[];
        CompanyRegistrationNumber?: string;
        TaxIdentificationNumber?: string;
        EinNumber?: string;
        EstimatedTransactionValueInUsd?: number;
        PciDssLevel?: number;
        FraudToolInPlace?: string;
        IsEdit?: boolean;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace SubmitBusinessAddress {
      type RequestI = {
        id?: string;
        homeAddress?: string;
        city?: string;
        zipCode?: string;
        stateOfResidence?: string;
        firstName?: string;
        dateOfBirth?: string;
        lastName?: string;
        countryOfResidence?: string;
        gender?: number;
        uboBusinessDocuments?: {
          idType: number;
          idNumber: string;
          uboIdentificationDocument: File;
          uboIdentificationDocumentBackSide: File;
        };
        homePhoneNumber?: string;
        email?: string;
        role?: string;
        shareSize?: number;
        socialSecurityNumber?: string;
        businessType?: number;
        citizenship?: string;
        occupation?: string;
      };
      [];

      export interface Request {
        requests: RequestI[];
        IsEdit?: boolean;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace SubmitBusinessDocuments {
      type OtherComplianceDoc = Partial<{
        Id: string;
        Title: string;
        OtherDocumentationType: File;
      }>;
      export interface Request {
        BusinessType?: number;
        CompanyRegistrationNumber?: string;
        TaxIdentificationNumber?: string;
        ShareHolderDocument?: File;
        CertificateOfIncorporation?: File;
        AgentOnboardingKYBForm?: File;
        AmlDocumentation?: File;
        OtherComplianceDocuments?: OtherComplianceDoc[];
        KybForm?: File;
        EinCertificate?: File;
        IrsCertificate?: File;
        CertificateOfGoodStanding?: File;
        BusinessDBA?: File;
        TaxDocument?: File;
        ProofOfAddress?: File;
        BoardResolution?: File;
        PartnershipAgreement?: File;
        CR12?: File;
        Memart?: File;
        IsEdit?: boolean;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace GetBusinessInformationCompletionCheck {
      export interface Response extends Response.ResponseBody {
        data?: {
          isBusinessInfomationCompleted: boolean;
          isBusinessAddressCompleted: boolean;
          isBusinessDocumentCompleted: boolean;
          isControlPersonCompleted: boolean;
          isSubmitted: boolean;
        };
      }
    }

    export namespace GetBusinessInformationDetails {
      export type AddressArray = Partial<{
        address: string;
        city: string;
        stateId: number;
        zipCode: string;
        registeredAddressEvidence: string;
      }>;

      type ControlPersonArray = Partial<{
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        gender: number;
        dateOfBirth: string;
        placeOfBirth: string;
        citizenship: string;
        countryOfResidence: string;
        occupation: string;
        stateOfResidence: number;
        city: string;
        zipCode: string;
        homeAddress: string;
        homePhoneNumber: string;
        idType: number;
        role: string;
        shareSize: number;
        socialSecurityNumber: string;
        uboBusinessDocuments: {
          idType: number;
          idNumber: string;
          uboIdentificationDocument: string;
          uboIdentificationDocumentBackSide: string;
        };
      }>;

      type otherComplianceDocsArray = Partial<{
        title: string;
        AmlDocumentation: string;
        otherDocumentationType?: string;
        id?: string;
      }>;

      export interface Response extends Response.ResponseBody {
        data: {
          businessInfomation: {
            name: string;
            tradeName: string;
            websiteUrl: string;
            companyRegistrationNumber: string;
            taxIdentificationNumber: string;
            phoneNumber: string;
            emailAddress: string;
            about: string;
            businessIndustry: string;
            businessType: string;
            address: AddressArray[];
            pciDssLevel: number;
            fraudToolInPlace: string;
            estimatedTransactionValueInUsd: number;
            tin: string;
            einNumber: string;
            logoUrl: string;
          };
          businessDocument: {
            certificateOfIncorporation: string;
            companyRegistrationNumber: string;
            taxIdentificationNumber: string;
            kybForm: string;
            einCertificate: string;
            irsCertificate: string;
            certificateOfGoodStanding: string;
            businessDBA: string;
            shareHolderDocument: string;
            amlDocumentation: string;
            agentOnboardingKYBForm: string;
            proofOfAddress: string;
            taxDocument: string;
            boardResolution: string;
            partnershipAgreement: string;
            cR12: string;
            memart: string;
            otherComplianceDocuments: otherComplianceDocsArray[];
          };
          controlPerson: ControlPersonArray[];
        };
      }
    }

    export namespace SubmitBusinessActivation {
      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }
  }

  /**
   * Recipients types
   */
  export namespace Recipients {
    export namespace GetPaymentTypesByCountry {
      export interface Request {
        country: Api.Model.CountryInitials;
      }
    }

    export namespace AddRecipient {
      export interface Payload {
        country: string;
        paymentType: string;
        bankCode?: string;
        accountName: string;
        accountNumber: string;
        routingNumber: string;
        firstName: string;
        middleName: string;
        lastName: string;
        dateOfBirth: string;
        phoneNumber: string;
        email: string;
        address: string;
        branchCode: string;
        paymentOperator: string;
        saveDetails: boolean;
      }

      export interface Request extends Api.Recipient.AddRecipient.Request {
        DateOfBirth: string;
      }
    }
  }

  /**
   * Bank types
   */
  export namespace Banks {
    export namespace GetBanks {
      export interface Request {
        country: Api.Model.CountryInitials;
      }
    }

    export namespace ResolveBankAccount {
      export interface Request {
        AccountNo: string;
        BankCode: string;
      }
    }

    export namespace GetBankBranches {
      export interface Request {
        // country: string;
        bankCode: string;
      }
    }

    export namespace GetModulrBanks {
      interface ModulrBankCapacityI {
        type: string;
        status: string;
      }

      export interface ModulrBankI {
        id: string;
        name: string;
        capabilities: ModulrBankCapacityI[];
      }

      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: ModulrBankI[];
      }
    }
  }

  /**
   * Transactions types
   */
  export namespace Transactions {
    export namespace CreateTransaction {
      interface GiftRequestPayload {
        DeliveryDate: string;
        OrderItemRequests:
          | {
              MerchantId: number | undefined;
              TemplateId: string;
              EventName: string;
              Amount: number;
              Quantity: number;
              RecipientEmail: string;
              RecipientPhone: string;
              RecipientFirstName: string;
              RecipientLastName: string;
            }[]
          | undefined;
      }

      interface WireFxRequest {
        aba: string;
        account: string;
        type: string;
      }

      export interface TransferScheduleDataI {
        transferType: string;
        name: string;
        duration: string;
        interval: string;
      }

      export interface Request {
        recipientId: number;
        amount: number;
        sendCurrency: Api.Model.CurrencyCode;
        receiveCurrency: Api.Model.CurrencyCode;
        securityQuestion: string;
        securityAnswer: string;
        transferScheduleData?: TransferScheduleDataI;
        gift?: GiftRequestPayload;
        chargeRoute?: string;
        wireFxRequest?: WireFxRequest;
        PIN: string;
        ModulrBank?: string;
        Voucher?: string;
        IsBusiness?: string;
      }
    }

    export namespace CreateBulkTransaction {
      interface DestinationI {
        amount: number;
        fee: number;
        currencyCode: string;
        branchCode: string;
        bankCode: string;
        accountNumber: string;
        description: string;
        firstName: string;
        lastName: string;
        middleName: string;
        emailAddress: string;
        routingNumber: string;
        phoneNumber: string;
        paymentType: string;
        paymentOperator: string;
        address: string;
      }

      export interface Request {
        /**
         * This is the ID of the selected payment type
         */
        paymentTypeId: number;
        /**
         * TransactionType
            None = 0
            Single = 1
            Bulk = 2
         */
        transactionType: number;
        /**
         * TransactionCategory
            Transfer = 1 
         */
        transactionCategory: number;
        totalAmount: number;
        totalFee: number;
        totalDestinationsCount: number;
        sendCurrencyCode: string;
        destinationCurrencyCode: string;
        destinationCountryCode: string;
        voucher: string;
        chargeRoute: string;
        pin: string;
        destinations: DestinationI[];
      }

      export interface Response extends Response.ResponseBody {
        data: {
          transactionReference: string;
          url?: string;
          amount?: number;
        };
      }
    }

    export namespace CreatePaymentLinkTransaction {
      // export interface Request {
      //   paymentReference: string;
      //   chargeRoute: string;
      //   payerFirstName: string;
      //   payerLastName: string;
      //   payerOtherName: string;
      //   payerEmailAddress: string;
      //   payerPhoneNumber: string;
      //   targetAmount: number;
      //   senderAmount: number;
      // }

      export interface Request {
        paymentReference: string;
        chargeRoute: string;
        payerFirstName: string;
        payerLastName: string;
        payerEmailAddress: string;
        payerPhoneNumber: string;
        targetAmount: number;
        senderAmount: number;
        senderCurrencyCode: string;
        modulrBank: string;
        wireFXRequest: {
          aba: string;
          account: string;
          type: string;
        };
        payerStateId: string;
        payerCity: string;
        payerAddress: string;
        payerPostCode: string;
        recurringPayment?: {
          interval: number;
          duration: number;
        };
      }

      export interface Response extends Response.ResponseBody {
        data: {
          transactionReference: string;
          url: string;
          amount: number;
        };
      }
    }

    export namespace CancelTransaction {
      export interface Request {
        TxRef: string;
        PaymentCode: string;
      }

      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: null;
      }
    }

    export namespace CancelBulkTransaction {
      export interface Request {
        txRef: string;
        paymentCode: string;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace GetTransactionByTxref {
      export interface Request {
        txref: string;
      }
    }

    export namespace CheckIfPinExists {
      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: null;
      }
    }

    export namespace CreatePin {
      export interface Request {
        PIN: string;
      }

      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: null;
      }
    }

    export namespace ChangePin {
      export interface Request {
        OldPIN: string;
        NewPIN: string;
        Password: string;
      }

      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: null;
      }
    }

    export namespace ResetPin {
      export interface Request {
        OTPCode: string;
        NewPIN: string;
        Password: string;
      }

      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: null;
      }
    }

    export namespace GetBulkTransactions {
      export interface Request {
        pageNumber: number;
        pageSize: number;
        /**
         * None = 0, 
           Week = 1, 
           Month = 2, 
           Year = 3, 
           AllTime = 4,
           CustomDate = 5
         */
        periodSelector: number;
        startDate?: string;
        endDate?: string;
        searchValue?: string;
      }

      interface Data extends Response.PaginatedDataResponse {
        transactions: General.BulkTransaction[];
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace GetBulkTransactionItems {
      export interface Request {
        transactionReference: string;
      }

      export interface Response extends Response.ResponseBody {
        data: {
          transactions: General.SingleTransaction[];
        };
      }
    }

    export namespace GetSingleTransactions {
      export interface Request {
        pageNumber: number;
        pageSize: number;
        /**
         * None = 0, 
           Week = 1, 
           Month = 2, 
           Year = 3, 
           AllTime = 4,
           CustomDate = 5
         */
        periodSelector: number;
        startDate?: string;
        endDate?: string;
        searchValue?: string;
        transactionType?: number;
      }

      interface Data extends Response.PaginatedDataResponse {
        transactions: General.SingleTransaction[];
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace GetSingleTransactionByReference {
      export interface Request {
        transactionReference: string;
        isRecipientUser?: boolean;
      }

      export interface Response extends Response.ResponseBody {
        data: {
          transactions: General.SingleTransaction[];
        };
      }
    }

    export namespace LoadVoucher {
      export interface Request {
        voucher: string;
      }
    }

    export namespace LoadPaymentCode {
      export interface Request {
        code: string;
      }

      export interface PaymentCodeI {
        Id: number;
        Code: string;
        Currency: string;
        TotalAmount: number;
        CurrentAmount: number;
        Description: string;
        OwnerId: number;
        Status: string;
        DateCreated: string | null;
        DateUpdated: string | null;
      }

      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: PaymentCodeI[];
      }
    }

    export namespace NethoneCheckByTxref {
      export interface Request {
        txref: string;
      }
    }

    export namespace GetSenderTransactionSummary {
      export interface TransactionSummaryI {
        status: string;
        counts: number;
      }

      export interface Response extends Response.ResponseBody {
        data: {
          total: number;
          processed: number;
          failed: number;
          pending: number;
        };
      }
    }

    export namespace GetPaymentLinkTransactions {
      export interface Request {
        pageNumber: number;
        pageSize: number;
        /**
         * None = 0, 
           Week = 1, 
           Month = 2, 
           Year = 3, 
           AllTime = 4,
           CustomDate = 5
         */
        periodSelector: number;
        startDate?: string;
        endDate?: string;
        searchValue?: string;
        linkReference: string;
      }

      export interface TransactionI {
        transactionReference: string;
        amountSent: number;
        amountReceived: number;
        fee: number;
        status: string;
        dateCreated: string;
        dateUpdated: string;
        sourceCurrency: {
          currencyCode: string;
          countryInitial: string;
          countryName: string;
        };
        targetCurrency: {
          currencyCode: string;
          countryInitial: string;
          countryName: string;
        };
        senderDetails: {
          firstName: string;
          lastName: string;
          email: string;
        };
      }

      interface Data extends Response.PaginatedDataResponse {
        transactions: TransactionI[];
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace GetReceiverTransactions {
      export interface Request {
        pageNumber: number;
        pageSize: number;
        /**
         * None = 0, 
           Week = 1, 
           Month = 2, 
           Year = 3, 
           AllTime = 4,
           CustomDate = 5
         */
        periodSelector: number;
        startDate?: string;
        endDate?: string;
        searchValue?: string;
        /**
         * None = 0
         * Single = 1
         * Bulk = 2
         */
        transactionType: 0 | 1 | 2;
        /**
         * Pass the value of boolean when trying to fetch settlement transactions
         */
        isSettled?: boolean;
      }

      export interface TransactionI {
        transactionReference: string;
        amountSent: number;
        amountReceived: number;
        fee: number;
        status: string;
        dateCreated: string;
        dateUpdated: string;
        sourceCurrency: {
          currencyCode: string;
          countryInitial: string;
          countryName: string;
        };
        targetCurrency: {
          currencyCode: string;
          countryInitial: string;
          countryName: string;
        };
        transactionClassification: string;
        paymentDeliveryMethod: string;
        senderDetails: {
          firstName: string;
          lastName: string;
          email: string;
        };
      }

      interface Data extends Response.PaginatedDataResponse {
        transactions: TransactionI[];
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace GetSettlementAccountTransactions {
      export interface Request {
        pageNumber: number;
        pageSize: number;
        /**
         * None = 0, 
           Week = 1, 
           Month = 2, 
           Year = 3, 
           AllTime = 4,
           CustomDate = 5
         */
        periodSelector: number;
        startDate?: string;
        endDate?: string;
        searchValue?: string;
        accountNumber: string;
      }

      export interface TransactionI {
        transactionReference: string;
        amountSent: number;
        amountReceived: number;
        fee: number;
        status: string;
        dateCreated: string;
        dateUpdated: string;
        sourceCurrency: {
          currencyCode: string;
          countryInitial: string;
          countryName: string;
        };
        targetCurrency: {
          currencyCode: string;
          countryInitial: string;
          countryName: string;
        };
        transactionClassification: string;
        paymentDeliveryMethod: string;
        senderDetails: {
          firstName: string;
          lastName: string;
          email: string;
        };
      }

      interface Data extends Response.PaginatedDataResponse {
        transactions: TransactionI[];
        accountDetails: General.SettlementAccountI;
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace GetReceiverTransactionSummary {
      export interface TransactionSummaryI {
        status: string;
        counts: number;
      }

      export interface Response extends Response.ResponseBody {
        data: {
          total: number;
          processed: number;
          failed: number;
          pending: number;
        };
      }
    }

    export namespace GetReceiverTransactionAmountsSummary {
      export interface AmountI {
        currency: string;
        amount: 0;
      }

      export interface Response extends Response.ResponseBody {
        data: {
          amounts: AmountI[];
        };
      }
    }

    export namespace GetPaymentLinkUsage {
      export interface Request {
        paymentLinkId: string;
      }

      export interface Response extends Response.ResponseBody {
        data: {
          totalPayment: number;
          pending: number;
          successful: number;
          failed: number;
          totalPaymentCount: number;
          pendingCount: number;
          successfulCount: number;
          failedCount: number;
        };
      }
    }

    export namespace GetPaymentSummaryCount {
      export interface Request {
        currency: string;
      }

      export interface Response extends Response.ResponseBody {
        data: {
          totalCollection: number;
          totalPayout: number;
          totalSettlement: number;
          totalCollectionCount: number;
          totalPayoutCount: number;
          totalSettlementCount: number;
        };
      }
    }
  }

  /**
   * Transactions types
   */
  export namespace Rate {
    export namespace CheckRateSprint {
      export interface Request {
        sendCurrency: string;
        receiveCurrency: string;
        amount?: number;
      }
    }
  }

  /**
   * User types
   */
  export namespace User {
    export namespace GetSettlementAccounts {
      export interface Request {
        pageNumber: number;
        pageSize: number;
      }

      interface Data extends Response.PaginatedDataResponse {
        accounts: General.SettlementAccountI[];
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace AddSettlementAccounts {
      export interface Request {
        paymentTypeId: number;
        bankName: string;
        bankCode: string;
        accountNumber: string;
        accountName: string;
        countryCode: string;
        accountCurrencyCode: string;
        setAsPrimary: boolean;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace UpdateSettlementAccount {
      export interface Request {
        paymentTypeId: number;
        bankName: string;
        bankCode: string;
        accountNumber: string;
        accountName: string;
        countryCode: string;
        accountCurrencyCode: string;
        setAsPrimary: boolean;
        id: number;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace DeleteSettlementAccount {
      export interface Request {
        AccountId: number;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace GetPrimarySettlementAccounts {
      export interface Response extends Response.ResponseBody {
        data: General.SettlementAccountI;
      }
    }

    export namespace AddPaymentLink {
      interface ExtraFieldI {
        name: string;
        value: string;
      }

      export interface Request {
        name: string;
        description: string;
        targetCurrencyCode: string;
        amount: number;
        extraFields: ExtraFieldI[];
        redirectUrl: string;
        frequency?: number;
        interval?: number;
        duration?: number;
        customerCanCancelSubscription?: boolean;
      }

      export interface Response extends Response.ResponseBody {
        data: {
          reference: string;
        };
      }
    }

    export namespace UpdatePaymentLink {
      interface ExtraFieldI {
        name: string;
        value: string;
      }

      export interface Request {
        reference: string;
        name: string;
        description: string;
        targetCurrencyCode: string;
        amount: number;
        extraFields: ExtraFieldI[];
        redirectUrl: string;
        frequency?: number;
        interval?: number;
        duration?: number;
        customerCanCancelSubscription?: boolean;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace DeletePaymentLink {
      export interface Request {
        PaymentLinkReference: string;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace GetPaymentLinks {
      export interface Request {
        pageNumber: number;
        pageSize: number;
        /**
         * None = 0, 
           Week = 1, 
           Month = 2, 
           Year = 3, 
           AllTime = 4,
           CustomDate = 5
         */
        periodSelector: number;
        startDate?: string;
        endDate?: string;
        searchValue?: string;
        status?: string;
        /**
         * 1 = single
         * 2 = recurring
         */
        frequency?: 1 | 2;
      }

      interface Data extends Response.PaginatedDataResponse {
        links: General.PaymentLinkI[];
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace UpdatePaymentLinkStatus {
      export interface Request {
        Reference: string;
        Disable: boolean;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace GetPaymentLinkDetails {
      export interface Request {
        Reference: string;
      }

      interface ExtraFieldI {
        name: string;
        value: string;
        status: string;
        dateCreated: string;
        dateUpdated: string;
      }

      export interface Response extends Response.ResponseBody {
        // data: {
        //   name: string;
        //   description: string;
        //   amount: number;
        //   senderCurrencyCode: string;
        //   targetCurrencyCode: string;
        //   status: string;
        //   reference: string;
        //   dateCreated: string;
        //   dateUpdated: string;
        //   extraFields: ExtraFieldI[];
        //   frequencyName: string;
        //   businessName: string;
        //   businessEmail: string;
        //   businessLogo: string;
        //   intervalName: string;
        //   duration: number;
        //   customerCanCancelSubscription: boolean;
        //   redirectUrl: null | string;
        // };
        data: General.PaymentLinkI;
      }
    }

    export namespace GetPaymentLinkBasicDetails {
      export interface Request {
        Reference: string;
      }

      interface ExtraFieldI {
        name: string;
        value: string;
        status: string;
        dateCreated: string;
        dateUpdated: string;
      }

      export interface Response extends Response.ResponseBody {
        data: {
          businessName: string;
          businessEmail: string;
          businessLogo: string;
          name: string;
          description: string;
          amount: number;
          senderCurrencyCode: string;
          targetCurrencyCode: string;
          extraFields: ExtraFieldI[];
          frequencyName: string;
          intervalName: string;
          duration: number;
          customerCanCancelSubscription: boolean;
          redirectUrl: string;
          userCategory: 'FintecOrg' | 'Receiver';
        };
      }
    }

    export namespace GetRoles {
      interface RoleI {
        id: number;
        name: string;
        description: string;
      }

      export interface Response extends Response.ResponseBody {
        data: {
          count: number;
          roles: RoleI[];
        };
      }
    }

    export namespace GetRoleSectionsPermissions {
      export interface Request {
        RoleId: string;
      }

      interface SectionPermissionsI {
        id: number;
        name: string;
        description: string;
      }

      export interface Response extends Response.ResponseBody {
        data: {
          count: number;
          sectionPermissions: SectionPermissionsI[];
        };
      }
    }

    export namespace GetProfileDetails {
      export interface Response extends Response.ResponseBody {
        data: {
          emailAddress: string;
          firstName: string;
          lastName: string;
          phoneNumber: string;
          referralCode: string;
          profilePixUrl: string;
          merchantId: number;
        };
      }
    }

    export namespace UpdateProfileDetails {
      export interface Request {
        firstName: string;
        lastName: string;
        phoneNumber: string;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace UploadProfilePhoto {
      export interface Request {
        DocFile: File;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace GetBusinessDetails {
      export interface Request {
        DocFile: string;
      }

      export interface Response extends Response.ResponseBody {
        data: {
          businessName: string;
          businessEmailAddress: string;
          businessPhoneNumber: string;
          businessPhysicalAddress: string;
          businessLogoUrl: string;
        };
      }
    }

    export namespace UpdateBusinessDetails {
      export interface Request {
        businessEmailAddress: string;
        businessPhoneNumber: string;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace UploadBusinessLogo {
      export interface Request {
        DocFile: File;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean;
      }
    }

    export namespace GetReceiverCustomers {
      export interface Request {
        pageNumber: number;
        pageSize: number;
        /**
         * None = 0, 
           Week = 1, 
           Month = 2, 
           Year = 3, 
           AllTime = 4,
           CustomDate = 5
         */
        periodSelector: number;
        startDate?: string;
        endDate?: string;
        searchValue?: string;
      }

      export interface CustomerI {
        emailAddress: string;
        firstName: string;
        lastName: string;
        phoneNumber: string;
      }

      interface Data extends Response.PaginatedDataResponse {
        customers: CustomerI[];
      }

      export interface Response extends Response.ResponseBody {
        data: Data;
      }
    }

    export namespace GetBusinessVerificationStatus {
      export interface Response extends Response.ResponseBody {
        data: {
          /**
            None = 0,
            Pending = 1,
            Verified = 2,
            Failed = 3
           */
          status: 0 | 1 | 2 | 3;
        };
      }
    }

    export namespace GetPaymentLinkSummary {
      export interface Response extends Response.ResponseBody {
        data: {
          totalLinks: number;
          numberOfActiveLinks: number;
          numberOfSingleActiveLinks: number;
          numberOfRecurringActiveLinks: number;
        };
      }
    }
  }

  export namespace ApiKeys {
    export interface ApiKey {
      Id: number;
      UserId: number;
      PublicKey: string;
      SecretKey: string;
      EncryptionKey: string;
      Status: string;
      DateCreated: string;
      DateUpdated: string;
    }

    export interface Webhook {
      Id: number;
      UserId: number;
      URL: string;
      Status: string;
      Retry: string;
      DateCreated: string;
      DateUpdated: string;
    }

    export namespace GenerateApiKeys {
      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: {
          PubKey: string;
          SecKey: string;
          EncKey: string;
        };
      }
    }

    export namespace LoadApiKeysByUser {
      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: ApiKey[];
      }
    }

    export namespace ActivateApiKey {
      export interface Request {
        Id: string;
      }

      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: null;
      }
    }

    export namespace DisableApiKey {
      export interface Request {
        Id: string;
      }

      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: null;
      }
    }

    export namespace CreateWebHook {
      export interface Request {
        URL: string;
      }

      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: any;
      }
    }

    export namespace LoadWebhookByUser {
      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: Webhook[];
      }
    }

    export namespace ActivateWebhook {
      export interface Request {
        Id: string;
      }

      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: any;
      }
    }

    export namespace DisableWebhook {
      export interface Request {
        Id: string;
      }

      export interface Response extends Api.Endpoint.Response.SuccessResponse {
        Data: any;
      }
    }
  }

  export namespace WhiteLabel {
    export namespace GetWhiteLabelConfigurations {
      export interface Response extends Response.ResponseBody {
        data: {
          isPublished: boolean;
          whiteLabelMediaFiles: {
            appIcon: string;
            appLogo: string;
            appFavicon: string;
            splashScreen: string;
          };
          whiteLabelStyling: {
            backgroundColor: string;
            primaryColor: string;
            buttonTextColor: string;
            primaryTextFont: string;
            secondaryTextFont: string;
            copyrightBusinessName: string;
            createdAt: string;
            updatedAt: string;
          };
          socialLinks: {
            instagramLink: string;
            linkedInLink: string;
            facebookLink: string;
            xLink: string;
          };
          smtpSettings: {
            useSendprintsService: boolean;
            smtpHost: string;
            portNumber: number;
            username: string;
            password: string;
            supportEmail: string;
            mailingEmail: string;
            fromEmail: string;
            replyEmail: string;
            noReplyEmail: string;
          };
          domainSettings: {
            hasDomain: boolean;
            domainName: string;
            dnsSetupCompleted: boolean;
            testServerIp: string;
            prodServerIp: string;
          };
          metadata: {
            footerText?: string;
            mobileAppName?: string;
            privacyPolicyLink?: string;
            termsAndConditionsLink?: string;
          };
        };
      }
    }

    export namespace SaveMediaFiles {
      export type Request = Partial<{
        AppIcon: File | string | null | undefined;
        AppLogo: File | string | null | undefined;
        AppFavicon: File | string | null | undefined;
        SplashScreen: File | string | null | undefined;
      }>;

      export interface Response extends Response.ResponseBody {
        data: any;
      }
    }

    export namespace SaveStylingConfiguration {
      export interface Request {
        backgroundColor: string;
        primaryColor: string;
        buttonTextColor: string;
        primaryTextFont: string;
        secondaryTextFont: string;
        copyrightBusinessName: string;
      }

      export interface Response extends Response.ResponseBody {
        data: any;
      }
    }

    export namespace SaveSocialLinks {
      export interface Request {
        instagramLink: string;
        linkedInLink: string;
        facebookLink: string;
        xLink: string;
      }

      export interface Response extends Response.ResponseBody {
        data: any;
      }
    }

    export namespace SaveSmtpSettings {
      export interface Request {
        useSendprintsService: boolean;
        smtpHost: string;
        portNumber: number;
        username: string;
        password: string;
        supportEmail: string;
        mailingEmail: string;
        fromEmail: string;
        replyEmail: string;
        noReplyEmail: string;
      }

      export interface Response extends Response.ResponseBody {
        data: any;
      }
    }

    export namespace SaveDomainSettings {
      export interface Request {
        hasDomain: boolean;
        domainName: string;
        dnsSetupCompleted: boolean;
      }

      export interface Response extends Response.ResponseBody {
        data: string;
      }
    }

    export namespace DomainNameCheck {
      export interface Request {
        domain: string;
      }

      export interface Response extends Response.ResponseBody {
        data: boolean | string;
      }
    }

    export namespace PublishWhiteLabel {
      export interface Response extends Response.ResponseBody {
        data: boolean | string;
      }
    }

    export namespace AddMetaData {
      export interface Request {
        metadata?: {
          footerText?: string;
          mobileAppName?: string;
          privacyPolicyLink?: string;
          termsAndConditionsLink?: string;
        };
      }

      export interface Response extends Response.ResponseBody {
        data: boolean | string;
      }
    }
  }

  export namespace AdminCustomer {
    export namespace GetCustomerAdvancedSearch {
      export interface Customer {
        TransactionStatus: boolean;
        PhoneNumberVerificationStatus: boolean;
        Id: number;
        FirstName: string;
        LastName: string;
        Email: string;
        PhoneNumber: string;
        DateCreated: string;
        DateOfBirth: string;
        LastLogin: string;
        CustomerStatus: string;
        CustomerType: string;
        SignUpSourceName: string;
        EmailVerificationStatus: true;
        IdentityVerificationStatus: boolean;
        AddressDetails: {
          Street: string;
          State: string;
          PostCode: string;
          Country: string;
          CountryName: string;
          City: string;
        };
      }

      export interface Recipient {
        Id: number | any;
        FirstName: string;
        MiddleName: string;
        LastName: string;
        Email: string;
        Phone: string;
        AccountNumber: string;
        BankCode: string;
        BranchCode: string;
        RoutingNumber: string;
        Address: string;
        Country: string;
        Status: string;
        PaymentType: string;
        PaymentOperator: string;
        DateCreated: string;
        DateUpdated: string;
        DateOfBirth: string;
        CustomerType: string;
        PaymentDeliveryMethod: string;
        IsDisable: true;
        CountryData: {
          CurrencyCode: string;
          CountryInitial: string;
          CountryName: string;
        };
      }

      export interface Request {
        SearchCriteria?: string;
        PageNumber?: number | any;
        PageSize?: number | any;
        IsFilter?: boolean | any;
        // CustomerType: 'Sender';
        CustomerType?: string;
        // CustomerStatus?: string;
        CustomerStatus?: string | any;
        // PeriodSelector?: string;
        PeriodSelector?: string | number;
        ResidentCountryCode?: string;
        // DisabledStatus?: string;
        DisabledStatus?: string;
        PaymentType?: string;
        StartDate?: string;
        EndDate?: string;
        RecipientStatus?: string;
        /**
         *  None = 0,
        Verified = 1,
        UnVerified = 2
         */
        IdentityVerificationStatus?: 0 | 1 | 2;
      }

      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: {
          Customers: Customer[];
          TotalNumberOfRecipientsReturned: number | any;
          Count: number | any;
          HasNextPage: true;
          PageNumber: number | any;
          TotalItemCount: number | any;
          Recipients: Recipient[];
          PendingCustomers: number;
          DeactivatedCustomers: number;
          ActiveCustomers: number;
          VerifiedCustomers: number;
          DeletedCustomers: number;
          SavedCustomers: number;
        };
      }
    }

    export namespace GetCustomerDetail {
      export interface Request {
        EmailAddress?: string;
      }
      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: {
          Id: string;
          BusinessId: string;
          Name: string;
          EmailVerificationStatus: boolean;
          IdentityVerificationStatus: boolean;
          PhoneNumberVerificationStatus: boolean;
          TransactionStatus: boolean;
          FirstName: string;
          LastName: string;
          Email: string;
          PhoneNumber: string;
          DateCreated: string;
          DateOfBirth: string;
          LastLogin: string;
          CustomerStatus: string;
          CustomerType: string;
          SignUpSourceName: string;
          AddressDetails: {
            Street: string;
            PostCode: string;
            Country: string;
            City: string;
            CountryName: string;
            State: string;
          };
        };
      }
    }

    export namespace UpdateCustomerStatus {
      export interface Request {
        Email?: string;
        Status?: string;
        Reason?: string;
      }
      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: boolean;
      }
    }

    export namespace GetCustomerActivities {
      export interface Log {
        UserStatus: string;
        Activity: string;
        UserName: string;
        EmailAddress: string;
        Target: string;
        DateCreated: string;
        RoleName: string;
        Reason: string;
      }

      export interface Request {
        PeriodSelector?: string;
        DateRange?: {
          StartDate: string;
          EndDate: string;
        };
        IsFilter?: true;
        SearchValue?: string;
        CustomerId?: number;
        PageNumber?: number;
        PageSize?: number;
      }

      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: {
          Logs: Log[];
          Count: 0;
          HasNextPage: true;
          PageNumber: 0;
          TotalItemCount: 0;
        };
      }
    }

    export namespace GetCustomerCounts {
      export interface Request {
        CustomerType: 'Sender' | 'Recipient';
      }

      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: {
          TotalCount: number;
          PendingCount: number;
          DeactivatedCount: number;
          ActiveCount: number;
          VerifiedCount: number;
          DeletedCount: number;
          SavedCount: number;
        };
      }
    }

    export namespace GetRecipientDetails {
      export interface Request {
        Id?: string;
      }
      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: {
          Id: number;
          FirstName: string;
          MiddleName: string;
          LastName: string;
          Email: string;
          Phone: string;
          AccountNumber: string;
          BankCode: string;
          BranchCode: string;
          RoutingNumber: string;
          Address: string;
          Country: string;
          Status: string;
          PaymentType: string;
          PaymentOperator: string;
          DateCreated: string;
          DateUpdated: string;
          DateOfBirth: string;
          CustomerType: string;
          PaymentDeliveryMethod: string;
          IsDisable: boolean;
          CountryData: {
            CurrencyCode: string;
            CountryInitial: string;
            CountryName: string;
          };
        };
      }
    }

    export namespace ManageRecipientStatus {
      export interface Request {
        BankCode?: string;
        CountryCode?: string;
        AccountNumber?: string;
        RoutingNumber?: string;
        ShouldDisable?: boolean;
        Reason?: string;
      }

      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: boolean;
      }
    }

    export namespace GetCustomerTransactionSummary {
      export interface Request {
        BankCode?: string;
        CountryCode?: string;
        AccountNumber?: string;
        RoutingNumber?: string;
      }
      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: {
          Successful: number | any;
          Pending: number | any;
          Failed: number | any;
          Total: number | any;
        };
      }
    }
  }

  export namespace AdminTransaction {
    export namespace GetTransactionCustomerSummary {
      export interface Request {
        SenderEmail?: string;
      }
      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: {
          TotalNumberOfTransactions: number;
          TransactionAmountSentGroup: any;
          TransactionStatusGroup: {
            Chargeback: number;
            Charged: number;
            Completed: number;
            Confirming: number;
            Declined: number;
            Disbursed: number;
            FailedCharge: number;
            FailedDisburse: number;
            Pending: number;
            Processing: number;
            Reversed: number;
          };
        };
      }
    }

    export namespace GetTransactionCustomerRecentTransactions {
      export interface ITransaction {
        TxRef: string;
        Amount: number | any;
        Fee: number | any;
        ChargedAmount: number | any;
        Rate: number | any;
        SourceCurrency: string;
        TargetCurrency: string;
        Status: string;
        DateCreated: string;
        DateUpdated: string;
        TransactionClassification: string;
        PaymentDeliveryMethod: string;
        RecipientDetails: {
          Id: number | any;
          FirstName: string;
          MiddleName: string;
          LastName: string;
          Email: string;
          Phone: string;
          AccountNumber: string;
          BankCode: string;
          BranchCode: string;
          RoutingNumber: string;
          Address: string;
          Country: string;
          Status: string;
          PaymentType: string;
          PaymentOperator: string;
          DateCreated: string;
          DateUpdated: string;
          DateOfBirth: string;
          CustomerType: string;
          PaymentDeliveryMethod: string;
          IsDisable: true;
          CountryData: {
            CurrencyCode: string;
            CountryInitial: string;
            CountryName: string;
          };
        };
        CustomerDetails: {
          FirstName: string;
          LastName: string;
          Email: string;
          PhoneNumber: string;
          DateCreated: string;
          DateOfBirth: string;
          LastLogin: string;
          CustomerStatus: string;
          CustomerType: string;
          SignUpSourceName: string;
        };
      }

      export interface Request {
        SenderEmail?: string;
        PageNumber?: string | any;
        PageSize?: string | any;
      }

      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: {
          TotalTransactions: number;
          Transactions: ITransaction[];
        };
      }
    }

    export namespace GetTransactionReceiverRecentTransactions {
      export interface Transaction {
        TxRef: string;
        Amount: number | any;
        Fee: number | any;
        ChargedAmount: number | any;
        Rate: number | any;
        SourceCurrency: string;
        TargetCurrency: string;
        Status: string;
        DateCreated: string;
        DateUpdated: string;
        TransactionClassification: string;
        PaymentDeliveryMethod: string;
        CustomerDetails: {
          FirstName: string;
          LastName: string;
          Email: string;
          PhoneNumber: string;
          DateCreated: string;
          DateOfBirth: string;
          LastLogin: string;
          CustomerStatus: string;
          CustomerType: string;
          SignUpSourceName: string;
        };
      }

      export interface Request {
        RecipientId?: string;
        PageNumber?: string | any;
        PageSize?: string | any;
      }
      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: {
          TotalTransactions: number | any;
          Transactions: Transaction[];
        };
      }
    }

    export namespace GetTransactionAdvancedSearch {
      export interface TransferTransaction {
        TransactionReference: string;
        DiscountCode: string;
        PaymentCode: string;
        AmountSent: number;
        AmountReceived: number;
        Fee: number;
        SourceCurrency: {
          Classification: string;
          CurrencyCode: string;
          CountryInitial: string;
          CountryName: string;
        };
        TargetCurrency: {
          Classification: string;
          CurrencyCode: string;
          CountryInitial: string;
          CountryName: string;
        };
        Status: string;
        DateCreated: string;
        DateUpdated: string;
        TransactionClassification: string;
        PaymentDeliveryMethod: string;
        BatchId: number;
        TransactionType: string;
        RecipientDetails: {
          Id: number;
          FirstName: string;
          MiddleName: string;
          LastName: string;
          Email: string;
          Phone: string;
          AccountNumber: string;
          BankCode: string;
          BranchCode: string;
          RoutingNumber: string;
          Address: string;
          Country: string;
          Status: string;
          PaymentType: string;
          PaymentOperator: string;
          DateCreated: string;
          DateUpdated: string;
          DateOfBirth: string;
          CustomerType: string;
          PaymentDeliveryMethod: string;
          IsDisable: boolean;
          CountryData: null;
        };
        CustomerDetails: {
          Id: number;
          FirstName: string;
          LastName: string;
          Email: string;
          PhoneNumber: string | null;
          DateCreated: string;
          DateOfBirth: string | null;
          LastLogin: string;
          CustomerStatus: string;
          CustomerType: string;
          SignUpSourceName: string | null;
          EmailVerificationStatus: boolean;
          IdentityVerificationStatus: boolean;
        };
        Revenue: number;
      }

      export interface GiftTransaction {
        TransactionReference: string;
        DiscountCode: string;
        AmountSent: number | any;
        AmountReceived: number | any;
        Fee: number | any;
        SourceCurrency: {
          Classification: string;
          CurrencyCode: string;
          CountryInitial: string;
          CountryName: string;
        };
        TargetCurrency: {
          Classification: string;
          CurrencyCode: string;
          CountryInitial: string;
          CountryName: string;
        };
        Status: string;
        DateCreated: string;
        DateUpdated: string;
        TransactionClassification: string;
        PaymentDeliveryMethod: string;
        BatchId: number | any;
        TransactionType: string;
        RecipientDetails: {
          Id: number | any;
          FirstName: string;
          MiddleName: string;
          LastName: string;
          Email: string;
          Phone: string;
          AccountNumber: string;
          BankCode: string;
          BranchCode: string;
          RoutingNumber: string;
          Address: string;
          Country: string;
          Status: string;
          PaymentType: string;
          PaymentOperator: string;
          DateCreated: string;
          DateUpdated: string;
          DateOfBirth: string;
          CustomerType: string;
          PaymentDeliveryMethod: string;
          IsDisable: true;
          CountryData: {
            CurrencyCode: string;
            CountryInitial: string;
            CountryName: string;
          };
        };
        CustomerDetails: {
          FirstName: string;
          LastName: string;
          Email: string;
          PhoneNumber: string;
          DateCreated: string;
          DateOfBirth: string;
          LastLogin: string;
          CustomerStatus: string;
          CustomerType: string;
          SignUpSourceName: string;
        };
        TransactionDetails: [
          {
            MerchantId: number | any;
            ItemCount: number | any;
            Amount: number | any;
            EventName: string;
            VoucherCode: [string];
            VoucherExpiryDate: string;
            DeliveryDate: string;
            TransactionReference: string;
            Status: string;
            GiftName: string;
            GiftImageUrl: string;
          }
        ];
        Revenue: number;
      }

      export interface Request {
        SearchCriteria?: string;
        PageNumber?: number | any;
        PageSize?: number | any;
        IsSearch?: boolean;
        IsFilter?: boolean;
        // TransactionClassification?: 'Transfer';
        TransactionClassification?: string;
        // PeriodSelector?:"None";
        PeriodSelector?: string;
        TransactionStatus?: string;
        SourceCurrencyCode?: string;
        TargetCurrencyCode?: string;
        StartDate?: string;
        EndDate?: string;
      }
      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: {
          Count: number;
          HasNextPage: boolean;
          PageNumber: number;
          TransferTransactionCount: number;
          TotalItemCount: number;
          GiftTransactionCount: number;
          GiftTransactions: GiftTransaction[];
          TransferTransactions: TransferTransaction[];
        };
      }
    }

    export namespace GetTransactionDetails {
      export interface Request {
        TransactionReference?: string;
      }
      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: {
          TransactionReference: string;
          DiscountCode: string;
          AmountSent: number | any;
          AmountReceived: number | any;
          PaymentCode: string;
          Fee: number;
          SourceCurrency: {
            Classification: string;
            CurrencyCode: string;
            CountryInitial: string;
            CountryName: string;
          };
          TargetCurrency: {
            Classification: string;
            CurrencyCode: string;
            CountryInitial: string;
            CountryName: string;
          };
          Status: string;
          DateCreated: string;
          DateUpdated: string;
          TransactionClassification: string;
          PaymentDeliveryMethod: string;
          BatchId: number | any;
          TransactionType: string;
          RecipientDetails: {
            Id: number | any;
            FirstName: string;
            MiddleName: string;
            LastName: string;
            Email: string;
            Phone: string;
            AccountNumber: string;
            BankCode: string;
            BranchCode: string;
            RoutingNumber: string;
            Address: string;
            Country: string;
            Status: string;
            PaymentType: string;
            PaymentOperator: string;
            DateCreated: string;
            DateUpdated: string;
            DateOfBirth: string;
            CustomerType: string;
            PaymentDeliveryMethod: string;
            IsDisable: true;
            CountryData: {
              CurrencyCode: string;
              CountryInitial: string;
              CountryName: string;
            };
          };
          CustomerDetails: {
            FirstName: string;
            LastName: string;
            Email: string;
            PhoneNumber: string;
            DateCreated: string;
            DateOfBirth: string;
            LastLogin: string;
            CustomerStatus: string;
            CustomerType: string;
            SignUpSourceName: string;
          };
          TransactionDetails: [
            {
              MerchantId: number | any;
              ItemCount: number | any;
              Amount: number | any;
              EventName: string;
              VoucherCode: [string];
              VoucherExpiryDate: string;
              DeliveryDate: string;
              TransactionReference: string;
              Status: string;
              GiftName: string;
              GiftImageUrl: string;
            }
          ];
          Revenue: number;
        };
      }
    }

    export namespace GetTransactionCounts {
      export interface Request {
        TransactionClassification: string;
      }

      export interface Response {
        ResponseCode: string;
        ResponseMessage: string;
        Data: {
          Total: number;
          Pending: number;
          Failed: number;
          Successful: number;
        };
      }
    }
  }

  export namespace AdminAuth {
    export namespace AdminRefreshToken {
      export interface Response extends Response.ResponseBody {
        Data: {
          SessionToken: string;
          SessionExpiryTime: string;
          SessionDuration: number;
        };
      }
    }
  }
}

export default ClientApi;
